<script setup>
import { reactive, ref } from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2'
import MultiSelect from 'primevue/multiselect'
import Calendar from 'primevue/calendar'
import dayjs from 'dayjs'
import format34 from '@/helpers/format34'

dayjs.extend(format34)

const props = defineProps({
  creators: {
    type: Object,
    required: true
  },
  department: {
    type: Object,
    required: true
  },
  cooperationSystemName: {
    type: String,
    required: true
  }
})
const data = reactive({
  isLoading: false,
  creatorReservations: [],
  diffReservations: [],
  fetched: {}
})
const selectedCreators = ref([])
const selectedFirstDate = ref('')
const selectedLastDate = ref('')
const formatStartTime = (startTime, endTime) => {
  if (startTime === null || endTime === null) return ''
  startTime = dayjs(startTime)
  endTime = dayjs(endTime)
  return endTime > endTime.startOf('day').add(10, 'hours') ||
    startTime >= startTime.startOf('day').add(10, 'hours')
    ? startTime.format('M/D HH:mm')
    : startTime.subtract(1, 'd').format('M/D h34:mm')
}
const formatEndTime = (endTime) => {
  if (endTime === null) return ''
  endTime = dayjs(endTime)
  return endTime > endTime.startOf('day').add(10, 'hours')
    ? endTime.format('M/D HH:mm')
    : endTime.subtract(1, 'd').format('M/D h34:mm')
}

const submit = async () => {
  const grandit_cds = selectedCreators.value.map(
    (creator) => creator['grandit_cd']
  )
  if (
    grandit_cds.length === 0 ||
    selectedFirstDate.value === '' ||
    selectedLastDate.value === ''
  ) {
    const modal = await Swal.fire({
      title: 'クリエイターと期間を指定してください',
      text: '',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      focusCancel: true
    })
    modal.isConfirmed
  } else if (dayjs(selectedLastDate.value) < dayjs(selectedFirstDate.value)) {
    const modal = await Swal.fire({
      title: '終了日は開始日以降を指定してください',
      text: '',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      focusCancel: true
    })
    modal.isConfirmed
  } else {
    try {
      data.isLoading = true
      const result = await axios.post(
        `/departments/${props.department.id}/reservations`,
        {
          grandit_cds: grandit_cds,
          first_date: dayjs(selectedFirstDate.value).format('YYYY-MM-DD'),
          last_date: dayjs(selectedLastDate.value).format('YYYY-MM-DD')
        }
      )
      data.creatorReservations = result['data']['creator_reservations']
      data.diffReservations = result['data']['diff_reservations']
      data.isLoading = false
    } catch (error) {
      data.isLoading = false
      await Swal.fire({
        icon: 'error',
        title: 'PANDAインポートに失敗しました',
        text: 'PANDA情報をご確認ください',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
    }
  }
}
</script>

<template>
  <vue-element-loading :active="data.isLoading" is-full-screen />
  <div class="flex flex-col">
    <div class="text-2xl font-semibold text-gray-800">PANDAインポート</div>
    <div class="flex flex-row">
      <div class="mb-3 ml-3 flex flex-col">
        <div class="mt-2 flex items-center text-sm text-gray-500">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="mr-1 h-4 w-4"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
            />
          </svg>
          {{ props.department.name }}
        </div>
      </div>
      <div class="mb-3 ml-3 flex flex-col">
        <div class="mt-2 flex items-center text-sm text-gray-500">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="mr-1 h-4 w-4"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
            />
          </svg>
          {{ props.cooperationSystemName }}
        </div>
      </div>
    </div>
  </div>
  <div class="flex-rows flex grid w-full grid-cols-3 items-end">
    <div class="col-span-2 flex flex-col">
      <MultiSelect
        v-model="selectedCreators"
        :options="creators"
        option-label="name"
        filter
        scroll-height="400px"
        placeholder="クリエイター選択"
        class="h-10 text-sm"
      />
      <div class="mt-4 flex flex-row items-center">
        <Calendar
          v-model="selectedFirstDate"
          class="mr-2 h-10"
          placeholder="開始日"
        />
        〜
        <Calendar
          v-model="selectedLastDate"
          class="mx-2 h-10"
          placeholder="終了日"
        />
        <button
          class="group ml-2 flex h-10 items-center justify-center rounded-md border border-gray-500 bg-white pl-3 pr-4 text-sm font-medium text-gray-800 shadow-sm hover:border-transparent hover:bg-gray-500 hover:text-white sm:w-auto"
          @click="submit"
        >
          PANDAデータプレビュー
        </button>
      </div>
    </div>
    <div class="flex justify-end">
      <ReservationImport
        :is-disabled-button="data.creatorReservations?.length === 0"
        :import-params="{
          grandit_cds: selectedCreators.map((creator) => creator['grandit_cd']),
          first_date: dayjs(selectedFirstDate).format('YYYY-MM-DD'),
          last_date: dayjs(selectedLastDate).format('YYYY-MM-DD')
        }"
        :department="props.department"
        :creator-reservations="data.creatorReservations"
      />
    </div>
  </div>
  <div v-if="data.diffReservations?.length !== 0">
    <div class="mt-2 flex flex-col">
      <div class="-mx-4 -my-2 overflow-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div
            class="overflow-hidden shadow ring-1 ring-gray-300 ring-opacity-80 md:rounded-lg"
          >
            <table class="min-w-full divide-y divide-gray-300">
              <thead class="bg-gray-50">
                <tr class="grid grid-cols-12">
                  <th
                    scope="col"
                    class="col-start-1 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-800 sm:pl-6"
                  ></th>
                  <th
                    scope="col"
                    class="col-start-3 px-3 py-3.5 text-left text-sm font-semibold text-gray-800"
                  >
                    開始時間
                  </th>
                  <th
                    scope="col"
                    class="col-start-4 px-3 py-3.5 text-left text-sm font-semibold text-gray-800"
                  >
                    終了時間
                  </th>
                  <th
                    scope="col"
                    class="col-start-5 px-3 py-3.5 text-left text-sm font-semibold text-gray-800"
                  >
                    部屋
                  </th>
                  <th
                    scope="col"
                    class="col-start-6 px-3 py-3.5 text-left text-sm font-semibold text-gray-800"
                  />
                  <th
                    scope="col"
                    class="col-start-7 px-3 py-3.5 text-left text-sm font-semibold text-gray-800"
                  >
                    得意先
                  </th>
                  <th
                    scope="col"
                    class="col-start-9 px-3 py-3.5 text-left text-sm font-semibold text-gray-800"
                  >
                    プロジェクト
                  </th>
                  <th
                    scope="col"
                    class="col-start-12 px-3 py-3.5 text-left text-sm font-semibold text-gray-800"
                  >
                    追加情報
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <div
                  v-for="creator_reservation in data.diffReservations"
                  :key="creator_reservation.name"
                >
                  <tr class="grid grid-cols-12">
                    <td
                      class="col-span-2 col-start-1 whitespace-pre-wrap break-words py-4 pl-4 pr-3 font-medium text-gray-800 sm:pl-6"
                    >
                      {{ creator_reservation.name }}
                    </td>
                    <td
                      class="col-span-10 col-start-3 whitespace-nowrap px-3 py-4 text-gray-800"
                    >
                      <table class="min-w-full divide-y divide-gray-300">
                        <tbody class="divide-y divide-gray-200 bg-white">
                          <div
                            v-for="reservation in creator_reservation.reservations"
                            :key="reservation.before"
                          >
                            <tr
                              class="flex grid grid-cols-10 flex-row justify-start"
                            >
                              <td
                                class="col-span-1 col-start-1 whitespace-pre-wrap break-words"
                              >
                                <ReservationDiff
                                  :before-value="
                                    formatStartTime(
                                      reservation.before.start_time,
                                      reservation.before.end_time
                                    )
                                  "
                                  :after-value="
                                    formatStartTime(
                                      reservation.after.start_time,
                                      reservation.after.end_time
                                    )
                                  "
                                />
                              </td>
                              <td
                                class="col-span-1 col-start-2 whitespace-pre-wrap break-words"
                              >
                                <ReservationDiff
                                  :before-value="
                                    formatEndTime(reservation.before.end_time)
                                  "
                                  :after-value="
                                    formatEndTime(reservation.after.end_time)
                                  "
                                />
                              </td>
                              <td
                                class="col-span-1 col-start-3 flex flex-col whitespace-pre-wrap break-words pl-1"
                              >
                                <ReservationDiff
                                  :before-value="reservation.before.studio_name"
                                  :after-value="reservation.after.studio_name"
                                />
                              </td>
                              <td
                                class="col-span-1 col-start-4 flex flex-col whitespace-pre-wrap break-words pl-1"
                              >
                                <ReservationDiff
                                  :before-value="
                                    reservation.before.resources_name
                                  "
                                  :after-value="
                                    reservation.after.resources_name
                                  "
                                />
                              </td>
                              <td
                                class="col-span-2 col-start-5 whitespace-pre-wrap break-words pl-1"
                              >
                                <ReservationDiff
                                  :before-value="
                                    reservation.before.project_name
                                  "
                                  :after-value="reservation.after.project_name"
                                />
                              </td>
                              <td
                                class="col-span-3 col-start-7 whitespace-pre-wrap break-words pl-2"
                              >
                                <ReservationDiff
                                  :before-value="
                                    reservation.before.project_customer
                                  "
                                  :after-value="
                                    reservation.after.project_customer
                                  "
                                />
                              </td>
                              <td
                                class="col-span-1 col-start-10 whitespace-pre-wrap break-words pl-4"
                              >
                                <ReservationDiff
                                  :before-value="
                                    reservation.before.reservation_name
                                  "
                                  :after-value="
                                    reservation.after.reservation_name
                                  "
                                />
                              </td>
                            </tr>
                          </div>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </div>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-2 flex justify-end">
      <ReservationImport
        :is-disabled-button="data.creatorReservations?.length === 0"
        :import-params="{
          grandit_cds: selectedCreators.map((creator) => creator['grandit_cd']),
          first_date: dayjs(selectedFirstDate).format('YYYY-MM-DD'),
          last_date: dayjs(selectedLastDate).format('YYYY-MM-DD')
        }"
        :department="props.department"
        :creator-reservations="data.creatorReservations"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-multiselect:not(.p-disabled).p-focus) {
  border-color: #f7d070;
  box-shadow: 0 0 0 1px #f7d070;
}
</style>
